/* ==========================================================================
   Vapor Space 2.0 — core
   --------------------------------------------------------------------------
   Shared primitives every other vs-*.js file builds on. Vanilla ES, no
   framework, no bundler, no dependencies. Loaded with `defer`.

   Exposes a single global, `window.VS`, which theme.liquid has already seeded
   with routes, strings and the free-delivery threshold. This file adds
   behaviour to it rather than replacing it.
   ========================================================================== */

(function () {
  'use strict';

  var VS = (window.VS = window.VS || {});

  /* --- Small helpers ----------------------------------------------------- */

  VS.$ = function (sel, root) {
    return (root || document).querySelector(sel);
  };

  VS.$$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* Event delegation. One listener on the document beats N listeners on N
     nodes, and it keeps working for markup swapped in by the Section
     Rendering API — which the cart does on every quantity change. */
  VS.on = function (event, selector, handler) {
    document.addEventListener(
      event,
      function (e) {
        var target = e.target.closest(selector);
        if (target) handler.call(target, e, target);
      },
      event === 'focus' || event === 'blur' ? true : false
    );
  };

  VS.prefersReducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* Money. Mirrors Shopify's money_format so JS-rendered prices match Liquid
     ones exactly. Only the placeholders Shopify actually emits are handled;
     an unknown format falls through returning the raw string rather than
     guessing and printing something wrong. */
  VS.formatMoney = function (cents) {
    var format = VS.moneyFormat || '{{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toLocaleString('en'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, whole.replace(/,/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/,/g, '.'));
  };

  /* --- Focus management --------------------------------------------------
     Anything that covers the page has to hold focus inside itself, or a
     keyboard user tabs straight through into the content behind it and the
     overlay becomes decorative. */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  VS.focusablesIn = function (root) {
    return VS.$$(FOCUSABLE, root).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  };

  /* --- Overlay -----------------------------------------------------------
     One controller for every panel that covers the page: cart drawer, mobile
     nav, search. They differ only in markup, so they share the behaviour:
     scroll lock, focus trap, Escape, restore focus to whatever opened it. */

  var openStack = [];

  function lockScroll() {
    if (openStack.length !== 1) return;
    // Compensate for the disappearing scrollbar so the page behind does not
    // jump sideways as the overlay opens.
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
  }

  function unlockScroll() {
    if (openStack.length !== 0) return;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  VS.Overlay = {
    open: function (el, opener) {
      if (!el || el.dataset.vsOpen === 'true') return;

      el.dataset.vsOpen = 'true';
      el.hidden = false;
      el.__opener = opener || document.activeElement;

      openStack.push(el);
      lockScroll();

      // Reflow before adding the class so the CSS transition actually runs
      // rather than being collapsed with the `hidden` removal.
      void el.offsetWidth;
      el.classList.add('is-open');

      if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'true');

      var focusables = VS.focusablesIn(el);
      var first = el.querySelector('[data-vs-autofocus]') || focusables[0];
      if (first) first.focus();

      document.dispatchEvent(new CustomEvent('vs:overlay:open', { detail: { el: el } }));
    },

    close: function (el) {
      if (!el || el.dataset.vsOpen !== 'true') return;

      el.dataset.vsOpen = 'false';
      el.classList.remove('is-open');

      var idx = openStack.indexOf(el);
      if (idx > -1) openStack.splice(idx, 1);

      var finish = function () {
        // Only hide if it has not been reopened in the meantime.
        if (el.dataset.vsOpen !== 'true') el.hidden = true;
        unlockScroll();
      };

      if (VS.prefersReducedMotion()) {
        finish();
      } else {
        var done = false;
        var once = function () {
          if (done) return;
          done = true;
          el.removeEventListener('transitionend', once);
          finish();
        };
        el.addEventListener('transitionend', once);
        // Transitionend does not fire if the element was never painted or the
        // property does not change. Always have a way out.
        setTimeout(once, 400);
      }

      var opener = el.__opener;
      if (opener) {
        if (opener.setAttribute) opener.setAttribute('aria-expanded', 'false');
        if (opener.focus) opener.focus();
      }

      document.dispatchEvent(new CustomEvent('vs:overlay:close', { detail: { el: el } }));
    },

    closeTop: function () {
      if (openStack.length) VS.Overlay.close(openStack[openStack.length - 1]);
    },
  };

  document.addEventListener('keydown', function (e) {
    if (!openStack.length) return;
    var top = openStack[openStack.length - 1];

    if (e.key === 'Escape') {
      e.preventDefault();
      VS.Overlay.close(top);
      return;
    }

    if (e.key !== 'Tab') return;

    var focusables = VS.focusablesIn(top);
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!top.contains(document.activeElement)) {
      // Focus escaped some other way; pull it back.
      e.preventDefault();
      first.focus();
    }
  });

  // Any element with data-vs-close inside an overlay closes it, as does a
  // click on the backdrop itself (but not on the panel within it).
  VS.on('click', '[data-vs-close]', function (e) {
    e.preventDefault();
    var overlay = this.closest('[data-vs-overlay]');
    if (overlay) VS.Overlay.close(overlay);
  });

  VS.on('click', '[data-vs-overlay]', function (e, overlay) {
    if (e.target === overlay) VS.Overlay.close(overlay);
  });

  /* --- Announcement rotator ----------------------------------------------
     Pauses on hover and on focus, and never starts at all under reduced
     motion. WCAG 2.2.2: auto-advancing text needs a way to stop, and this
     band carries the free-delivery threshold, which people need time to read.
     ---------------------------------------------------------------------- */

  VS.$$('[data-vs-rotator]').forEach(function (root) {
    var items = VS.$$('[data-rotator-item]', root);
    if (items.length < 2) return;

    var index = 0;
    var timer = null;
    var interval = parseInt(root.dataset.interval, 10) || 6000;

    function show(next) {
      items[index].classList.remove('is-active');
      items[index].setAttribute('aria-hidden', 'true');

      index = (next + items.length) % items.length;

      items[index].classList.add('is-active');
      items[index].removeAttribute('aria-hidden');
    }

    function start() {
      if (timer || VS.prefersReducedMotion()) return;
      timer = setInterval(function () {
        show(index + 1);
      }, interval);
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    // Stop entirely when the tab is hidden — no point burning timers, and it
    // avoids a burst of queued advances on return.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    var prev = VS.$('[data-rotator-prev]', root);
    var next = VS.$('[data-rotator-next]', root);

    if (prev)
      prev.addEventListener('click', function () {
        stop();
        show(index - 1);
      });

    if (next)
      next.addEventListener('click', function () {
        stop();
        show(index + 1);
      });

    start();
  });
})();
